"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractClient = exports.defaults = void 0;
const tslib_1 = require("tslib");
/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
const axios_1 = tslib_1.__importDefault(require("axios"));
const qs = tslib_1.__importStar(require("query-string"));
const types_1 = require("./types");
const utils_1 = require("./utils");
exports.defaults = {
    headers: {
        Accept: 'application/json'
    },
    paramsSerializer: {
        serialize: (params) => qs.stringify(params, {
            arrayFormat: 'none',
            skipNull: true,
            skipEmptyString: true
        })
    }
};
class AbstractClient {
    constructor(options) {
        Object.defineProperty(this, "agent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "applicationToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "locale", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "preview", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "baseHost", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "basePath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        const { baseURL, baseHost, basePath } = this.determineClientURL(options);
        const agentDefaults = (0, utils_1.mergeConfigs)({
            ...exports.defaults,
            baseURL
        }, options?.defaults);
        this.agent = options?.agent || axios_1.default.create(agentDefaults);
        this.applicationToken = options?.applicationToken;
        this.locale = options?.locale;
        this.preview = options?.preview;
        this.baseHost = baseHost;
        this.basePath = basePath;
    }
    getAgent() {
        return this.agent;
    }
    /**
     * Executes an HTTP request call.
     *
     * @param config the request config
     * @param [options] additional options to customize the call
     * @return the HTTP response
     */
    async call(config, options) {
        this.attachAccessTokenIfPresent(config, options?.accessToken);
        this.attachApplicationTokenIfPresent(config, options?.applicationToken || this.applicationToken);
        this.attachAcceptLanguageHeaderIfPresent(config, options?.locale || this.locale);
        this.attachPreviewOptionsIfPresent(config, options?.preview || this.preview);
        this.attachCancelTokenIfPresent(config, options?.cancelToken);
        config = (0, utils_1.mergeConfigs)(config, { headers: options?.headers }, { params: options?.params }, options?.config);
        if (options?.configCustomizer) {
            await options.configCustomizer(config);
        }
        return await this.getAgent().request(config);
    }
    /**
     * Attaches an interceptor function to the axios request interceptor manager
     * and returns a function that will detach it.
     *
     * @param onFulfilled the function that executes before the request executes
     * @param onRejected the function that executes when the request fails to execute
     * @return a function to detach the interceptor
     */
    requestInterceptor(onFulfilled, onRejected) {
        const interceptorHandle = this.getAgent().interceptors.request.use(onFulfilled, onRejected);
        return () => this.getAgent().interceptors.request.eject(interceptorHandle);
    }
    /**
     * Attaches an interceptor function to the axios response interceptor manager
     * and returns a function that will detach it.
     *
     * @param onFulfilled the function that executes after a successful response
     * @param onRejected the function that executes after a failure response
     * @return a function to detach the interceptor
     */
    responseInterceptor(onFulfilled, onRejected) {
        const interceptorHandle = this.getAgent().interceptors.response.use(onFulfilled, onRejected);
        return () => this.getAgent().interceptors.response.eject(interceptorHandle);
    }
    determineClientURL(options) {
        if (options?.baseURL) {
            const url = new URL(options.baseURL);
            return {
                baseURL: options.baseURL,
                baseHost: `${url.origin}${url.host}`,
                basePath: url.pathname
            };
        }
        else {
            return {
                baseURL: this.createBaseURL(options.baseHost, options.basePath),
                baseHost: options.baseHost,
                basePath: options.basePath
            };
        }
    }
    createBaseURL(baseHost, basePath) {
        if (baseHost) {
            if (basePath) {
                return `${baseHost}${basePath}`;
            }
            return baseHost;
        }
        return basePath;
    }
    attachAccessTokenIfPresent(config, accessToken) {
        if (accessToken) {
            if (!config.headers) {
                config.headers = {};
            }
            if (accessToken.startsWith('Bearer ')) {
                config.headers[types_1.DefaultRequestHeaderType.AUTHORIZATION] = accessToken;
            }
            else {
                config.headers[types_1.DefaultRequestHeaderType.AUTHORIZATION] = `Bearer ${accessToken}`;
            }
        }
    }
    attachApplicationTokenIfPresent(config, applicationToken) {
        if (applicationToken) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers[types_1.DefaultRequestHeaderType.APPLICATION_TOKEN] =
                applicationToken;
        }
    }
    attachAcceptLanguageHeaderIfPresent(config, locale) {
        if (locale) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers[types_1.DefaultRequestHeaderType.ACCEPT_LANGUAGE] = locale;
        }
    }
    attachCancelTokenIfPresent(config, cancelToken) {
        if (cancelToken) {
            config.cancelToken = cancelToken;
        }
    }
    attachPreviewOptionsIfPresent(config, preview) {
        if (preview) {
            if (!config.headers) {
                config.headers = {};
            }
            const { sandbox, token, date } = preview;
            if (sandbox) {
                config.headers[types_1.DefaultRequestHeaderType.PREVIEW_SANDBOX] = sandbox;
            }
            if (token) {
                config.headers[types_1.DefaultRequestHeaderType.PREVIEW_TOKEN] = token;
            }
            if (date) {
                config.headers[types_1.DefaultRequestHeaderType.PREVIEW_DATE] = date;
            }
        }
    }
}
exports.AbstractClient = AbstractClient;
//# sourceMappingURL=client.js.map